import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import md5 from 'md5';
import { useStateCallback } from 'site-modules/shared/hooks/use-state-callback';
import { useSafeId } from 'site-modules/shared/hooks/use-safe-id';
import { AnimatedCollapse } from 'client/site-modules/shared/utils/collapse-utils';

import './accordion.scss';

export function Accordion({
  iconCollapsed = 'icon-arrow-right4',
  iconExpanded = 'icon-arrow-down4',
  children,
  className = null,
  tabClasses = 'py-0_5',
  chevronClasses = 'size-10 text-primary-darker me-0_75 px-0',
  headerClasses = 'text-gray-darker size-16 text-start fw-normal m-0 p-0',
  itemClasses = 'ms-1_5 ps-0_25',
  headingLabelComponent = 'span',
  tagsConfig: {
    accordionTag: AccordionTag,
    fragmentTag: FragmentTag,
    labelTag: LabelTag,
    headingTag: HeadingTag,
    contentTag: ContentTag,
  } = {
    accordionTag: 'dl',
    fragmentTag: Fragment,
    headingTag: Fragment,
    labelTag: 'dt',
    contentTag: 'dd',
  },
  onExpand = null,
  allowMultipleOpen = false,
}) {
  const accordionId = useSafeId();
  const [openSections, setOpenSections] = useStateCallback(
    children.reduce((sections, child) => {
      // eslint-disable-next-line no-param-reassign
      sections[child.props.id] = child.props.isOpen;
      return sections;
    }, {})
  );

  function toggle({
    currentTarget: {
      dataset: { name: id },
    },
  }) {
    const isExpanded = !openSections[id];
    setOpenSections({ ...(allowMultipleOpen ? openSections : {}), [id]: isExpanded }, () => {
      if (onExpand && isExpanded) {
        onExpand(id);
      }
    });
  }

  const HeadingLabel = headingLabelComponent;

  return (
    <AccordionTag className={classnames('accordion', className)}>
      {children.map(child => {
        const componentId = `${accordionId}-${md5(child.props.id)}`;
        return (
          <FragmentTag key={child.props.id}>
            <LabelTag>
              <HeadingTag>
                <button
                  data-name={child.props.id}
                  className={classnames('accordion-btn d-flex w-100', tabClasses)}
                  onClick={toggle}
                  data-tracking-id={child.props.trackingId}
                  aria-expanded={!!openSections[child.props.id]}
                  aria-controls={componentId}
                >
                  <i
                    className={classnames('chevron', chevronClasses, {
                      [iconCollapsed]: !openSections[child.props.id],
                      [iconExpanded]: openSections[child.props.id],
                    })}
                    aria-hidden
                  />
                  <HeadingLabel
                    className={classnames('heading-label', headerClasses, {
                      'fw-bold': !!openSections[child.props.id],
                    })}
                  >
                    {child.props.label}
                  </HeadingLabel>
                </button>
              </HeadingTag>
            </LabelTag>
            <AnimatedCollapse
              isOpen={!!openSections[child.props.id]}
              elementType={ContentTag}
              className="mb-0"
              id={componentId}
            >
              <div className={classnames('my-0_5 size-16', itemClasses)}>{child.props.children}</div>
            </AnimatedCollapse>
          </FragmentTag>
        );
      })}
    </AccordionTag>
  );
}

Accordion.propTypes = {
  allowMultipleOpen: PropTypes.bool,
  iconCollapsed: PropTypes.string,
  iconExpanded: PropTypes.string,
  children: PropTypes.instanceOf(Object).isRequired,
  className: PropTypes.string,
  tabClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  chevronClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  headerClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  itemClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  headingLabelComponent: PropTypes.string,
  onExpand: PropTypes.func,
  tagsConfig: PropTypes.shape({
    accordionTag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
    fragmentTag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
    headingTag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
    labelTag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
    contentTag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
  }),
};
